import { useSearch } from '@tanstack/react-router';
import { createContext, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import type { Crumb } from '../components/page-breadcrumbs';
import { useExperiment } from '../hooks/use-experiment';
import { useRun } from '../hooks/use-run';
import { useRunProgress } from '../hooks/use-run-progress';
import { useRunTitle } from '../hooks/use-run-title';

import { RUN_TABS, type RunTab } from './run-tabs';

/**
 * Where a cross-section link's destination came from (S8 "context-carrying navigation" —
 * #158/#159: clicking a product from a batch landed on a listing page with no trace of which
 * batch/run sent you there). Encoded into the URL's `from` param as a short string (never a raw
 * id-only blob) so a bookmarked/shared link degrades gracefully — `parseNavOrigin` drops anything
 * it doesn't recognise rather than failing navigation.
 */
export type NavOrigin =
  | { kind: 'batch'; runId: string; tab: RunTab }
  | { kind: 'attention' }
  | { kind: 'experiment'; experimentId: string; tab: string }
  | { kind: 'history'; productId: string }
  /** The Batches page's failure-patterns view (S12, `?view=failures`) — a batch link out of a
   *  pattern row shows "← Back to Failure patterns" rather than the plain "← Back to Batches". */
  | { kind: 'failurePatterns' };

const BATCH_FROM_RE = /^batch:([^:]+):([^:]+)$/;
const EXPERIMENT_FROM_RE = /^experiment:([^:]+):([^:]+)$/;
const HISTORY_FROM_RE = /^history:(.+)$/;

/** Every `RunTab` value, for validating a `batch:` token's tab segment. */
const RUN_TAB_VALUES = new Set<string>(RUN_TABS.map((t) => t.value));

/**
 * Parses the `from` URL token into a typed origin. An unrecognised shape, an unknown run tab, or
 * a missing id returns `undefined` ("unknown → ignored", design round 3 S8) so a stale or
 * hand-edited link never breaks the destination page — it just renders as if opened directly.
 */
export function parseNavOrigin(from: string | undefined): NavOrigin | undefined {
  if (!from) return undefined;
  if (from === 'attention') return { kind: 'attention' };
  if (from === 'failure-patterns') return { kind: 'failurePatterns' };
  const batch = BATCH_FROM_RE.exec(from);
  if (batch) {
    const [, runId, rawTab] = batch;
    // Option 2 — the old "Your decision" tab (`gate`) is now the panel on Products; the old
    // Publishing tab (`approval`) is folded into Summary (2026-10-16).
    const tab = rawTab === 'gate' ? 'products' : rawTab === 'approval' ? 'overview' : rawTab;
    if (!runId || !tab || !RUN_TAB_VALUES.has(tab)) return undefined;
    return { kind: 'batch', runId, tab: tab as RunTab };
  }
  const experiment = EXPERIMENT_FROM_RE.exec(from);
  if (experiment) {
    const [, experimentId, tab] = experiment;
    if (!experimentId || !tab) return undefined;
    return { kind: 'experiment', experimentId, tab };
  }
  const history = HISTORY_FROM_RE.exec(from);
  if (history?.[1]) return { kind: 'history', productId: history[1] };
  return undefined;
}

/** Encodes a typed origin back into the `from` URL token — the inverse of `parseNavOrigin`. */
export function encodeNavOrigin(origin: NavOrigin): string {
  switch (origin.kind) {
    case 'batch':
      return `batch:${origin.runId}:${origin.tab}`;
    case 'attention':
      return 'attention';
    case 'experiment':
      return `experiment:${origin.experimentId}:${origin.tab}`;
    case 'history':
      return `history:${origin.productId}`;
    case 'failurePatterns':
      return 'failure-patterns';
  }
}

/**
 * The `{ from, back }` search shape every destination route's `validateSearch` spreads in
 * (product page, listing page, product-in-batch page, needs attention, experiment pages).
 * `back` must stay an in-app catalog-enrichment path — anything else (or malformed) is dropped rather
 * than failing navigation, same graceful-degradation rule as `from`.
 */
export const navContextSearchSchema = {
  from: z.string().optional().catch(undefined),
  back: z
    .string()
    .refine((v) => v.startsWith('/catalog-enrichment/'))
    .optional()
    .catch(undefined),
  /** Optional name of the exact place `back` returns to (e.g. an Insights row, "Description ·
   *  Readability") — the back chip then reads "← Back to <it>" instead of the tab's name. */
  backLabel: z.string().max(160).optional().catch(undefined),
};

export interface NavContextSearch {
  from?: string | undefined;
  back?: string | undefined;
  backLabel?: string | undefined;
}

/**
 * Builds the `{ from, back }` pair to attach to a `Link`'s `search` when leaving a page toward a
 * cross-section destination. `backHref` is normally the CURRENT page's own address
 * (`location.href`, pathname + search) so the destination's "← Back to <origin>" chip restores
 * whatever tab/filters were open, not just the bare route.
 */
export function withFrom(origin: NavOrigin, backHref?: string): NavContextSearch {
  return { from: encodeNavOrigin(origin), ...(backHref ? { back: backHref } : {}) };
}

/**
 * Ambient origin for every cross-link rendered under a batch (`RunShell`), the needs-attention
 * queue, or an experiment's detail page — so a shared leaf component (a product cell, a flag
 * badge, a commerce-cell link) never needs its origin threaded through as a prop: it just reads
 * whichever of those three providers is its nearest ancestor. `undefined` outside all three
 * (e.g. the product master list itself), where a link is same-section navigation and needs no
 * `from` at all.
 */
export const NavOriginCtx = createContext<{ origin: NavOrigin; backHref: string } | undefined>(
  undefined,
);

/**
 * The `{ from, back }` search for a cross-link leaving the CURRENT page: an explicit origin (when
 * this one link's origin differs from its ambient one — e.g. the journey drawer, opened from
 * either a batch's rows or a product's own history) wins; otherwise the nearest `NavOriginCtx`
 * provider's origin; otherwise `{}` (today's behaviour, unchanged).
 */
export function useCrossLinkSearch(explicit?: NavOrigin): NavContextSearch {
  const ambient = useContext(NavOriginCtx);
  if (explicit) return withFrom(explicit);
  if (!ambient) return {};
  return withFrom(ambient.origin, ambient.backHref);
}

/** The "← Back to <origin>" chip's target: either an already-built href (restores the origin's
 *  exact tab + filters) or a `to`/`params` fallback (the origin's base route) when none was
 *  recorded. */
export interface BackTarget {
  label: string;
  to?: string | undefined;
  params?: Record<string, string> | undefined;
  href?: string | undefined;
}

/** `useNavContext`'s resolved shape — named so `useMemo` below can check each branch against it
 *  directly instead of inferring one wide union first (which otherwise muddies `back`'s shape). */
interface NavContextResult {
  origin: NavOrigin | undefined;
  originCrumbs: Crumb[];
  back: BackTarget | undefined;
  /** The link's own name for where Back returns (`backLabel`), when it sent one. */
  backLabel: string | undefined;
}

/**
 * Reads this DESTINATION page's own `from`/`back` search params (every page that spreads
 * `navContextSearchSchema` into its route's `validateSearch`) and resolves them into: the typed
 * origin, a ready-made breadcrumb prefix (design: "Batches › batch › tab › …"), and the "← Back
 * to <origin>" chip's label + target. Names are resolved from cache-or-fetch (never an id) —
 * `useRun`/`useExperiment` are the same shared queries their own pages use, so the label is
 * usually already warm from whichever page linked here.
 */
export function useNavContext(): NavContextResult {
  const { t } = useTranslation('catalog-enrichment');
  const { t: tRun } = useTranslation('catalog-enrichment-run');
  const { from, back: backHref, backLabel }: NavContextSearch = useSearch({ strict: false });
  const origin = parseNavOrigin(from);

  const batchRunId = origin?.kind === 'batch' ? origin.runId : undefined;
  const runQuery = useRun(batchRunId ?? '', !!batchRunId);
  const progressQuery = useRunProgress(batchRunId ?? '');
  const { title: runTitle } = useRunTitle(
    batchRunId ? runQuery.data : undefined,
    batchRunId ? progressQuery.data : undefined,
  );

  const experimentId = origin?.kind === 'experiment' ? origin.experimentId : undefined;
  const experimentQuery = useExperiment(experimentId);

  const resolved = useMemo((): Omit<NavContextResult, 'backLabel'> => {
    if (!origin) return { origin: undefined, originCrumbs: [], back: undefined };

    if (origin.kind === 'batch') {
      const tabMeta = RUN_TABS.find((tb) => tb.value === origin.tab);
      const tabLabel = tabMeta ? tRun(`catalog-enrichment-run:${tabMeta.labelKey}`) : null;
      const originCrumbs: Crumb[] = [
        { label: t('breadcrumbs.runs'), to: '/catalog-enrichment/runs' },
        { label: runTitle, to: '/catalog-enrichment/runs/$runId', params: { runId: origin.runId } },
        ...(tabMeta && tabMeta.value !== 'overview' && tabLabel
          ? [{ label: tabLabel, to: tabMeta.to, params: { runId: origin.runId } }]
          : []),
      ];
      return {
        origin,
        originCrumbs,
        back: {
          label: t('navContext.backToBatch', { name: runTitle }),
          to: (tabMeta ?? RUN_TABS[0]).to,
          params: { runId: origin.runId },
          href: backHref,
        },
      };
    }

    if (origin.kind === 'attention') {
      return {
        origin,
        originCrumbs: [
          { label: t('breadcrumbs.needsAttention'), to: '/catalog-enrichment/needs-attention' },
        ],
        back: {
          label: t('navContext.backToAttention'),
          to: '/catalog-enrichment/needs-attention',
          href: backHref,
        },
      };
    }

    if (origin.kind === 'failurePatterns') {
      return {
        origin,
        originCrumbs: [
          {
            label: t('breadcrumbs.failurePatterns'),
            to: '/catalog-enrichment/runs',
            search: { view: 'failures' },
          },
        ],
        back: {
          label: t('navContext.backToFailurePatterns'),
          to: '/catalog-enrichment/runs',
          href: backHref,
        },
      };
    }

    if (origin.kind === 'experiment') {
      const name = experimentQuery.data?.name ?? t('navContext.experimentFallback');
      return {
        origin,
        originCrumbs: [
          { label: t('breadcrumbs.experiments'), to: '/catalog-enrichment/experiments' },
          {
            label: name,
            to: '/catalog-enrichment/experiments/$experimentId',
            params: { experimentId: origin.experimentId },
          },
        ],
        back: {
          label: t('navContext.backToExperiment', { name }),
          to: '/catalog-enrichment/experiments/$experimentId',
          params: { experimentId: origin.experimentId },
          href: backHref,
        },
      };
    }

    // 'history'
    return {
      origin,
      originCrumbs: [{ label: t('breadcrumbs.products'), to: '/catalog-enrichment/products' }],
      back: {
        label: t('navContext.backToHistory'),
        to: '/catalog-enrichment/products/$productId',
        params: { productId: origin.productId },
        href: backHref,
      },
    };
  }, [origin, runTitle, experimentQuery.data?.name, backHref, t, tRun]);
  return useMemo(() => ({ ...resolved, backLabel }), [resolved, backLabel]);
}
